import { apiFetch } from '../api/client'
// Military units overlay + plans: fetch helpers and MIL-STD-2525D rendering.

import ms from 'milsymbol'
import { PR_API_BASE } from './regionStats'

/** Deployed units of the caller's own country (unit positions are private —
 * the server scopes the list to the SSO session, like /map/my-region-stats). */
export const fetchMilitaryDeployments = async () => {
  const resp = await apiFetch(`${PR_API_BASE}/military/deployments`, {
    credentials: 'include',
  })
  if (!resp.ok) throw new Error(`military-deployments HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('military-deployments fetch failed')
  return payload.data || []
}

/** Own ORBAT (tree + stats) — used for the "Mes unités" deploy panel. */
export const fetchOrbat = async (countryId) => {
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/military/orbat`, { credentials: 'include' })
  if (!resp.ok) throw new Error(`orbat HTTP ${resp.status}`)
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error('orbat fetch failed')
  return payload.data
}

/** Deploy or move a unit to world coordinates. */
export const deployUnit = async (countryId, unitId, lat, lon) => {
  const resp = await apiFetch(
    `${PR_API_BASE}/countries/${countryId}/military/units/${unitId}/deploy`,
    { credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat, lon }),
    }
  )
  const payload = await resp.json()
  if (!payload || !payload.success) throw new Error(payload?.error || 'deploy failed')
  return payload
}

// ── Military plans (standalone map files, shareable) ─────────────────────

export const fetchPlans = async (countryId) => {
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/military/plans`, { credentials: 'include' })
  const payload = await resp.json()
  return payload?.data || []
}

export const fetchPlan = async (countryId, planId) => {
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/military/plans/${planId}`, { credentials: 'include' })
  const payload = await resp.json()
  return payload?.data || null
}

export const fetchSharedPlan = async (token) => {
  const resp = await apiFetch(`${PR_API_BASE}/military/plans/shared/${encodeURIComponent(token)}`, { credentials: 'include' })
  const payload = await resp.json()
  return payload?.success ? payload.data : null
}

export const createPlan = async (countryId, data) => {
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/military/plans`, { credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  const payload = await resp.json()
  return payload?.data || null
}

export const updatePlan = async (countryId, planId, data) => {
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/military/plans/${planId}`, { credentials: 'include',
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  const payload = await resp.json()
  return payload?.data || null
}

export const deletePlan = async (countryId, planId) => {
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/military/plans/${planId}`, { credentials: 'include',
    method: 'DELETE',
  })
  return (await resp.json())?.success === true
}

export const sharePlan = async (countryId, planId, enable = true) => {
  const resp = await apiFetch(
    `${PR_API_BASE}/countries/${countryId}/military/plans/${planId}/share`,
    { credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enable }),
    }
  )
  const payload = await resp.json()
  return payload?.data || null
}

/** Saved designer symbols of the country (free symbol placement in plans). */
export const fetchSymbols = async (countryId) => {
  const resp = await apiFetch(`${PR_API_BASE}/countries/${countryId}/military/symbols`, { credentials: 'include' })
  const payload = await resp.json()
  return payload?.data || []
}

// ── Symbol rendering ─────────────────────────────────────────────────────

/**
 * SVG markup for a unit marker: the stored designer symbol when the unit has
 * one, otherwise a friendly land-unit symbol with the echelon amplifier.
 */
export const unitSymbolSvg = (unit, size = 34) => {
  if (unit.symbol && unit.symbol.svg) return unit.symbol.svg
  const sidc = unit.symbol?.sidc
    || `10031000${unit.echelon?.sidc_amplifier || '00'}1211000000`
  try {
    return new ms.Symbol(sidc, {
      size,
      uniqueDesignation: unit.symbol?.unique_designation || '',
    }).asSVG()
  } catch {
    return '<div style="font-size:20px">🪖</div>'
  }
}

/** Render an arbitrary SIDC (plan markers). */
export const sidcGraphic = (sidc, size = 30) => {
  try {
    const symbol = new ms.Symbol(sidc, { size })
    return { svg: symbol.asSVG(), size: symbol.getSize(), anchor: symbol.getAnchor() }
  } catch {
    return { svg: '<div style="font-size:18px">❓</div>', size: { width: size, height: size }, anchor: { x: size / 2, y: size / 2 } }
  }
}

export const sidcSvg = (sidc, size = 30) => sidcGraphic(sidc, size).svg

/**
 * Category of a unit for the customizable layer toggles. Derived from the
 * branch code when set, else from the symbol SIDC symbol set / entity.
 */
export const unitCategory = (unit) => {
  const branch = unit.branch?.code
  if (branch === 'air_force') return 'air'
  if (branch === 'navy') return 'naval'
  if (branch === 'special_forces') return 'special'
  if (branch === 'logistics') return 'support'
  const sidc = unit.symbol?.sidc || ''
  const set = sidc.slice(4, 6)
  if (set === '01') {
    return sidc.slice(10, 16) === '110200' ? 'helicopters' : 'air'
  }
  if (set === '30' || set === '35') return 'naval'
  const entity = sidc.slice(10, 16)
  if (entity === '121700') return 'special'
  if (['160200', '161100', '161300', '163600', '111000'].includes(entity)) return 'support'
  if (entity === '120600') return 'helicopters'
  return 'ground'
}

export const UNIT_CATEGORIES = [
  { key: 'ground', label: '🪖 Terrestre' },
  { key: 'air', label: '✈️ Avions' },
  { key: 'helicopters', label: '🚁 Hélicoptères' },
  { key: 'naval', label: '⚓ Naval' },
  { key: 'special', label: '🗡️ Forces spéciales' },
  { key: 'support', label: '📦 Soutien' },
]
